import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from 'react-apollo';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Input from '@material-ui/core/Input';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import SendIcon from '@material-ui/icons/Send';
import Spin from 'components/common/Spin';
import usePromise from 'hooks/usePromise';
import { ADD_COMMENT, GET_COMMENTS } from 'graphql/comment';
import concat from 'lodash/concat';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'fixed',
    right: 20,
    bottom: 90,
    backgroundColor: 'white',
    borderRadius: 8,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    borderLeft: '1px solid #eee',
    height: 'calc(100vh - 70px)',
    maxHeight: 500,
    width: 360,
    boxShadow: '0px 4px 40px rgba(98, 108, 130, 0.15)',
  },
  header: {
    backgroundColor: '#AAB5CE',
    color: 'white',
    padding: 16,
  },
  list: {
    padding: 10,
    flex: 1,
    overflowY: 'auto',
  },
  bubbleSelf: {
    'marginLeft': '10%',
    'marginBottom': 24,
    '& .header': {
      textAlign: 'right',
    },
    '& .content': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      borderRadius: 8,
      padding: 10,
    },
  },
  bubbleOther: {
    'marginRight': '10%',
    'marginBottom': 24,
    'backgroundColor': 'white',
    '& .header': {
      display: 'flex',
      justifyContent: 'space-between',
    },
    '& .content': {
      backgroundColor: '#E8EBF1',
      borderRadius: 8,
      padding: 10,
    },
  },
  author: {
    fontWeight: 700,
  },
  ts: {
    textAlign: 'right',
  },
  input: {
    paddingLeft: 8,
    paddingTop: 10,
    paddingBottom: 10,
  },
  inputContainer: {
    borderTop: '1px solid #ccc',
  },
}));

const Comment = ({ comment, userId, classes }) => {
  const isSelf = userId === comment.user_id;
  return (
    <div className={isSelf ? classes.bubbleSelf : classes.bubbleOther}>
      <div className="header">
        {!isSelf ? (
          <Typography gutterBottom variant="caption" className={classes.author}>
            {`${comment.user.user_first_name} ${comment.user.user_last_name}`}
          </Typography>
        ) : (
          <span />
        )}
        <Typography
          className={classes.ts}
          variant="caption"
          color="textSecondary"
        >
          {moment(comment.created_at).format('LLL')}
        </Typography>
      </div>
      <div className="content">
        <Typography gutterBottom color="inherit" variant="body2">
          {comment.comment_text}
        </Typography>
      </div>
    </div>
  );
};

const Comments =React.forwardRef(({ exerciseId, userId }, ref) => {
  const classes = useStyles();
  const [text, setText] = useState('');
  const { data, loading } = useQuery(GET_COMMENTS, {
    variables: { exerciseId },
  });
  const listRef = useRef();
  const [saveComment] = useMutation(ADD_COMMENT);
  const onSave = async () => {
    await saveComment({
      variables: {
        input: [
          {
            comment_text: text,
            student_exercise_id: exerciseId,
            user_id: userId,
          },
        ],
      },
      update: (
        cache,
        {
          data: {
            comments: { returning },
          },
        },
      ) => {
        const { comments } = cache.readQuery({
          query: GET_COMMENTS,
          variables: { exerciseId },
        });
        cache.writeQuery({
          query: GET_COMMENTS,
          variables: { exerciseId },
          data: {
            comments: concat(comments, returning),
          },
        });
      },
    });
    setText('');
  };

  const [handleSave, saving] = usePromise(onSave);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [data]);

  return (
    <div className={classes.root} ref={ref}>
      <div className={classes.header}>
        <Typography variant="subtitle1">Comments</Typography>
      </div>
      {loading ? (
        <Spin spinning />
      ) : (
        <div className={classes.list} ref={listRef}>
          {data.comments.map((c) => (
            <Comment comment={c} userId={userId} classes={classes} />
          ))}
        </div>
      )}
      <div className={classes.inputContainer}>
        <Input
          multiline
          fullWidth
          disableUnderline
          className={classes.input}
          margin="none"
          placeholder="Type your comments/questions here…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rowsMax={4}
          endAdornment={
            <InputAdornment position="end" onClick={handleSave}>
              <IconButton color="primary" disabled={!text}>
                {saving ? <Spin spinning /> : <SendIcon />}
              </IconButton>
            </InputAdornment>
          }
        />
      </div>
    </div>
  );
});

export default Comments;
